import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingFarmResourceConsumption,
  useMutateFootprintingFarmResourceConsumption,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, InformationCard, InputNumber, PageSpin } from "components";

type FarmElectricityFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
  setIsSaveComplete?: (isSaveComplete: boolean) => void;
};

export const FarmElectricityForm = ({ primaryDataId, farmId, year, readOnly, setIsSaveComplete }: FarmElectricityFormProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);
  const carbonProgram = useGetCarbonProgramByFarmId(farmId);

  const farmResourceConsumptionQuery = useGetFootprintingFarmResourceConsumption(primaryDataId ?? "");

  const [electricityKwh, setElectricityKwh] = useState<number | null>(null);
  const [renewablesPct, setRenewablesPct] = useState<number | null>(null);
  const [dairyElectricityPct, setDairyElectricityPct] = useState<number | null>(null);

  const { mutate: mutateFarmResourceConsumption } = useMutateFootprintingFarmResourceConsumption(
    farmId,
    year,
    {
      id: farmResourceConsumptionQuery.data?.id,
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      electricityKwh,
      renewablesPct,
      dairyElectricityPct,
    },
    primaryDataId
  );

  const isLoading = queryIsLoading(farmResourceConsumptionQuery);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        setIsSaveComplete?.(false);
        mutateFarmResourceConsumption(undefined, {
          onSuccess: () => {
            setIsSaveComplete?.(true);
          },
          onError: () => {
            setIsSaveComplete?.(true);
          },
        });
      }
    },
    [isLoading, mutateFarmResourceConsumption, readOnly, setIsSaveComplete]
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (farmResourceConsumptionQuery.data) {
      setElectricityKwh(farmResourceConsumptionQuery.data.electricityKwh ?? null);
      setDairyElectricityPct(farmResourceConsumptionQuery.data.dairyElectricityPct ?? null);
      setRenewablesPct(farmResourceConsumptionQuery.data.renewablesPct ?? null);

      formRef.current?.setFieldsValue({
        electricityKwh: farmResourceConsumptionQuery.data.electricityKwh ?? null,
        dairyElectricityPct: formatFractionInput(farmResourceConsumptionQuery.data.dairyElectricityPct ?? null),
        renewablesPct: formatFractionInput(farmResourceConsumptionQuery.data.renewablesPct ?? null),
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = (["electricityKwh", "renewablesPct", "dairyElectricityPct"] as const).some(
        (field) => farmResourceConsumptionQuery.data[field] !== null
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [farmResourceConsumptionQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200">{t("electricityForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("electricityForm.info")}</p>
        </Flex>
      </Card>

      <Card dataTestId="footprinting-farm-electricity-form" className="padding-200">
        <InformationCard
          iconName=""
          showIcon={false}
          text={t("electricityForm.subText")}
          title={""}
          variant={"neutral"}
          className="margin-bottom-100 sub-text-information-card"
        />
        <Form ref={formRef}>
          <Flex vertical gap="1rem">
            <Form.Item>
              <InputNumber
                className="floating-label"
                dataTestId="footprinting-farm-electricity-input"
                disabled={readOnly}
                name="electricityKwh"
                label={t("electricityForm.question.total")}
                tooltip={t("electricityForm.tooltipTotal")}
                suffix="kWh"
                value={electricityKwh}
                initialValue={electricityKwh}
                onChange={(v) => setElectricityKwh(v)}
                rules={[
                  { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                  { type: "number", message: " " },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("electricityForm.error.total")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Form.Item>
            {carbonProgram.data?.platformFeatures?.footprinting?.dairy === true ? (
              <Form.Item validateTrigger={["onBlur", "onInput"]}>
                <InputNumber
                  className="floating-label"
                  dataTestId="footprinting-farm-dairy-percentage-input"
                  disabled={readOnly}
                  name="dairyElectricityPct"
                  label={t("electricityForm.question.dairyPercentage")}
                  tooltip={t("electricityForm.tooltipDairyPercentage")}
                  suffix="%"
                  value={formatFractionInput(dairyElectricityPct)}
                  initialValue={formatFractionInput(dairyElectricityPct)}
                  onChange={(v) => setDairyElectricityPct(formatPercentageInput(v))}
                  precision={2}
                  rules={[
                    { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                    { type: "number", message: " " },
                    {
                      validator: (_: unknown, v: number) => {
                        if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                          return Promise.reject(new Error(t("electricityForm.error.dairyPercentage")));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
              </Form.Item>
            ) : null}
            <Form.Item validateTrigger={["onBlur", "onInput"]}>
              <InputNumber
                className="floating-label"
                dataTestId="footprinting-farm-renewables-input"
                disabled={readOnly}
                name="renewablesPct"
                label={t("electricityForm.question.renewables")}
                tooltip={t("electricityForm.tooltipRenewables")}
                suffix="%"
                value={formatFractionInput(renewablesPct)}
                initialValue={formatFractionInput(renewablesPct)}
                onChange={(v) => setRenewablesPct(formatPercentageInput(v))}
                precision={2}
                rules={[
                  { required: true, message: " " }, // message is an empty to stop duplication error message showing up with the input name
                  { type: "number", message: " " },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                        return Promise.reject(new Error(t("electricityForm.error.renewables")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Form.Item>
          </Flex>
        </Form>
      </Card>
    </div>
  );
};
